import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import Modal from "react-modal";
import { read } from "xlsx";

import { getAllUsergroups } from "../../../../../firebase";
import { setNotification } from "redux/actions/notifications";
import { NotificationVariant } from "redux/reducers/notifications";
import { modalStyles } from "styles/modals";
import { generatePageTitle } from "utils/general.utils";
import {
  applyCompanyResolution,
  isCreatable,
  isValidEmailFormat,
  normalizeEmail,
  parseClientSheet,
  summarizeRows,
  validateClientRows,
} from "utils/clientUpload.utils";
import { Usergroup } from "types/firebase";
import { FormOption } from "types/index";
import {
  ClientCreateResult,
  ClientUploadRow,
  EmailCheckState,
  ParseClientSheetResult,
} from "types/clientUpload";

import StandardButton from "components/Common/buttons/StandardButton";
import Spinner from "components/Common/misc/Spinner";
import ClientPreviewTable, { isDone } from "./ClientPreviewTable";
import { checkExistingEmails, createClientAccount } from "./clientUploadApi";

type SheetSummary = {
  fileName: string;
  sheetName: string;
  cutoffRow: number;
  pdfSkipped: number;
  blankSkipped: number;
};

const UploadClients: React.FC = () => {
  const dispatch = useDispatch();
  const [usergroups, setUsergroups] = useState<Usergroup[]>([]);
  const [loadingGroups, setLoadingGroups] = useState<boolean>(true);
  const [groupsError, setGroupsError] = useState<string>("");
  const [parseError, setParseError] = useState<string>("");
  const [sheetSummary, setSheetSummary] = useState<SheetSummary | null>(null);
  // Rows (including passwords) stay in component state only, never in Redux
  const [rows, setRows] = useState<ClientUploadRow[]>([]);
  const [emailChecks, setEmailChecks] = useState<{
    [email: string]: EmailCheckState;
  }>({});
  const [checkError, setCheckError] = useState<string>("");
  const [showPasswords, setShowPasswords] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  const [stopping, setStopping] = useState<boolean>(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState<{ [id: string]: ClientCreateResult }>(
    {}
  );
  const stopRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    document.title = generatePageTitle("Upload Clients");
  });

  useEffect(() => {
    Modal.setAppElement("#root");

    return () => {
      mountedRef.current = false;
      stopRef.current = true;
    };
  }, []);

  // Loaded fresh (not from the persisted Redux copy) so seat counts are current
  const loadUsergroups = useCallback(async () => {
    setLoadingGroups(true);
    setGroupsError("");

    try {
      const groups: Usergroup[] = await getAllUsergroups();
      if (mountedRef.current) setUsergroups(groups);
    } catch (error) {
      if (mountedRef.current) {
        setGroupsError("Could not load companies. Refresh the page to try again.");
      }
    } finally {
      if (mountedRef.current) setLoadingGroups(false);
    }
  }, []);

  useEffect(() => {
    loadUsergroups();
  }, [loadUsergroups]);

  const validatedRows = useMemo(
    () => validateClientRows(rows, { usergroups, emailChecks }),
    [rows, usergroups, emailChecks]
  );

  const companyOptions: FormOption[] = useMemo(
    () => [
      { id: 0, value: "", text: "--- Select a company ---" },
      ...usergroups
        .filter((ug) => ug.id)
        .sort((a, b) => a.company.localeCompare(b.company))
        .map((ug, index) => ({
          id: index + 1,
          value: ug.id as string,
          text: ug.company,
        })),
    ],
    [usergroups]
  );

  const openRows = validatedRows.filter((row) => !isDone(results[row.id]));
  const counts = summarizeRows(openRows);
  const creatableRows = openRows.filter(isCreatable);
  const checking = Object.values(emailChecks).includes("pending");
  const hasFailedChecks = Object.values(emailChecks).includes("failed");
  // Seat counts must be fresh before creating (they change after each run)
  const canCreate =
    creatableRows.length > 0 && !checking && !loadingGroups && !groupsError;
  const resultList = Object.values(results);
  const createdRows = validatedRows.filter(
    (row) => results[row.id] && results[row.id].state === "created"
  );
  const notLinkedRows = validatedRows.filter(
    (row) => results[row.id] && results[row.id].state === "profile-failed"
  );

  const resetUpload = () => {
    setParseError("");
    setSheetSummary(null);
    setRows([]);
    setEmailChecks({});
    setCheckError("");
    setResults({});
    setProgress({ done: 0, total: 0 });
  };

  const runEmailChecks = async (emails: string[]) => {
    const unique = Array.from(new Set(emails)).filter(isValidEmailFormat);
    if (unique.length === 0) return;

    const markAll = (state: EmailCheckState) =>
      setEmailChecks((prev) => {
        const next = { ...prev };
        unique.forEach((email) => (next[email] = state));
        return next;
      });

    setCheckError("");
    markAll("pending");

    try {
      const { existing, invalid } = await checkExistingEmails(unique);
      if (!mountedRef.current) return;

      setEmailChecks((prev) => {
        const next = { ...prev };
        unique.forEach((email) => {
          if (existing.includes(email)) next[email] = "exists";
          else if (invalid.includes(email)) next[email] = "invalid";
          else next[email] = "new";
        });
        return next;
      });
    } catch (err) {
      if (!mountedRef.current) return;
      const status = (err as any)?.response?.status;

      setCheckError(
        status === 403
          ? "Not authorized to check accounts. Your session may have expired, or your account is not on the server admin allowlist."
          : "Could not check for existing accounts."
      );
      markAll("failed");
    }
  };

  const retryEmailChecks = () => {
    const emails = rows
      .map((row) => normalizeEmail(row.email))
      .filter((email) => emailChecks[email] !== "new" && emailChecks[email] !== "exists");
    runEmailChecks(emails);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    // Allows choosing the same file again
    e.target.value = "";
    if (!file) return;

    resetUpload();

    let parsed: ParseClientSheetResult;
    try {
      const workbook = read(await file.arrayBuffer(), { type: "array" });
      parsed = parseClientSheet(workbook);
    } catch (error) {
      setParseError("Could not read this file as an Excel workbook.");
      return;
    }

    if (!parsed.ok) {
      setParseError(parsed.error);
      return;
    }

    const candidates = applyCompanyResolution(parsed.candidates, usergroups);

    setSheetSummary({
      fileName: file.name,
      sheetName: parsed.sheetName,
      cutoffRow: parsed.cutoffRow,
      pdfSkipped: parsed.pdfSkipped,
      blankSkipped: parsed.blankSkipped,
    });
    setRows(candidates);
    runEmailChecks(candidates.map((row) => row.email));
  };

  const updateRow = (id: string, patch: Partial<ClientUploadRow>) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  };

  const handleEmailBlur = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;

    const email = normalizeEmail(row.email);
    if (email !== row.email) updateRow(id, { email });
    const check = emailChecks[email];
    if (isValidEmailFormat(email) && (!check || check === "failed")) {
      runEmailChecks([email]);
    }
  };

  const handleCreate = async () => {
    setConfirmOpen(false);
    if (!canCreate) return;

    const queue = creatableRows;
    const groupsById: { [id: string]: Usergroup } = {};
    usergroups.forEach((ug) => {
      if (ug.id) groupsById[ug.id] = ug;
    });

    stopRef.current = false;
    setStopping(false);
    setCreating(true);
    setProgress({ done: 0, total: queue.length });

    let created = 0;
    let attempted = 0;

    for (const row of queue) {
      if (stopRef.current) break;

      const group = groupsById[row.companyId as string];
      const email = normalizeEmail(row.email);

      setResults((prev) => ({ ...prev, [row.id]: { state: "creating" } }));

      const result = await createClientAccount({
        name: row.name.trim(),
        email,
        password: row.password,
        company: group.company,
        companyId: group.id as string,
      });

      if (!mountedRef.current) return;

      attempted++;
      if (result.state === "created") created++;

      setResults((prev) => ({ ...prev, [row.id]: result }));
      setProgress({ done: attempted, total: queue.length });

      if (
        result.state === "created" ||
        result.state === "exists" ||
        result.state === "profile-failed"
      ) {
        setEmailChecks((prev) => ({ ...prev, [email]: "exists" }));
      }

      if (result.state === "unauthorized") break;
    }

    setCreating(false);
    setStopping(false);

    dispatch(
      setNotification({
        message: `Created ${created} of ${queue.length} client accounts.`,
        variant:
          created === queue.length
            ? NotificationVariant.success
            : NotificationVariant.warning,
      })
    );

    // Refresh seat counts for any further rows
    loadUsergroups();
  };

  const handleStop = () => {
    stopRef.current = true;
    setStopping(true);
  };

  const handleStartOver = () => {
    resetUpload();
  };

  return (
    <Container>
      <PageTitle>Upload Clients</PageTitle>
      <hr />
      <Intro>
        Upload the IDO Login Credentials sheet (.xlsx). Only rows below the last
        row marked <strong>Added = Yes</strong> are new. PDF rows are skipped.
        Review and edit the rows below, then create the accounts. Nothing is
        created until you confirm.
      </Intro>

      {loadingGroups && !creating ? (
        <Spinner fullscreen={false} />
      ) : (
        <FileInput>
          <input
            type="file"
            name="clientSheet"
            accept=".xlsx"
            onChange={handleFileChange}
            disabled={creating || !!groupsError}
          />
        </FileInput>
      )}

      {groupsError && <ErrorText>{groupsError}</ErrorText>}
      {parseError && <ErrorText>{parseError}</ErrorText>}

      {sheetSummary && (
        <>
          <Summary>
            <span>
              File: <strong>{sheetSummary.fileName}</strong> ({sheetSummary.sheetName})
            </span>
            <span>
              Cut-off: row <strong>{sheetSummary.cutoffRow}</strong> (last Added = Yes)
            </span>
            <span>
              New rows: <strong>{rows.length}</strong>
            </span>
            <span>PDF rows skipped: {sheetSummary.pdfSkipped}</span>
            <span>Blank rows ignored: {sheetSummary.blankSkipped}</span>
          </Summary>

          {rows.length === 0 ? (
            <p>
              There are no new rows below row {sheetSummary.cutoffRow}. Add new
              clients below the last "Added = Yes" row and upload again.
            </p>
          ) : (
            <>
              <Summary>
                <span>Ready: {counts.ready}</span>
                <span>Warnings: {counts.warning}</span>
                <span>Will not be created: {counts.error}</span>
                <span>Excluded: {counts.excluded}</span>
                {checking && <span>Checking existing accounts...</span>}
              </Summary>

              {hasFailedChecks && (
                <ErrorText>
                  {checkError || "Could not check some emails for existing accounts."}{" "}
                  <LinkButton type="button" onClick={retryEmailChecks}>
                    Retry check
                  </LinkButton>
                </ErrorText>
              )}

              <label>
                <input
                  type="checkbox"
                  checked={showPasswords}
                  onChange={() => setShowPasswords(!showPasswords)}
                />{" "}
                Show passwords
              </label>

              <ClientPreviewTable
                rows={validatedRows}
                companyOptions={companyOptions}
                results={results}
                showPasswords={showPasswords}
                disabled={creating}
                onChange={updateRow}
                onEmailBlur={handleEmailBlur}
              />

              <Actions>
                {creating ? (
                  <>
                    <p>
                      Creating {Math.min(progress.done + 1, progress.total)} of{" "}
                      {progress.total}...
                    </p>
                    {stopping ? (
                      <p>Stopping after the current row...</p>
                    ) : (
                      <StandardButton
                        type="button"
                        size="md"
                        color="grey"
                        onClick={handleStop}
                      >
                        Stop after current row
                      </StandardButton>
                    )}
                  </>
                ) : canCreate ? (
                  <StandardButton
                    type="button"
                    size="md"
                    color="green"
                    onClick={() => setConfirmOpen(true)}
                  >
                    Create {creatableRows.length} account
                    {creatableRows.length === 1 ? "" : "s"}
                  </StandardButton>
                ) : (
                  <p>
                    {checking
                      ? "Waiting for the existing-account check..."
                      : loadingGroups
                      ? "Refreshing companies..."
                      : groupsError
                      ? "Companies could not be loaded, so accounts cannot be created."
                      : "No rows are ready to create. Fix or include rows above."}
                  </p>
                )}
              </Actions>
            </>
          )}
        </>
      )}

      {!creating && resultList.length > 0 && (
        <Results>
          <h3>Results</h3>
          <p>
            Created: {createdRows.length} · Already existed:{" "}
            {resultList.filter((r) => r.state === "exists").length} · Failed:{" "}
            {
              resultList.filter(
                (r) => r.state === "failed" || r.state === "unauthorized"
              ).length
            }{" "}
            · Profile not linked: {notLinkedRows.length}
          </p>
          {createdRows.length > 0 && (
            <p>
              <strong>Mark these sheet rows as Added = Yes:</strong>{" "}
              {createdRows.map((row) => row.sheetRow).join(", ")}
            </p>
          )}
          {notLinkedRows.length > 0 && (
            <p>
              <strong>Needs attention</strong> (login created, profile not
              linked): rows {notLinkedRows.map((row) => row.sheetRow).join(", ")}
            </p>
          )}
          <StandardButton
            type="button"
            size="md"
            outline
            onClick={handleStartOver}
          >
            Upload another file
          </StandardButton>
        </Results>
      )}

      <Modal
        isOpen={confirmOpen}
        contentLabel="Create client accounts"
        style={modalStyles}
        onRequestClose={() => setConfirmOpen(false)}
      >
        <ModalContent>
          <h3>
            Create {creatableRows.length} client account
            {creatableRows.length === 1 ? "" : "s"}?
          </h3>
          <ul>
            {creatableRows.map((row) => (
              <li key={row.id}>
                Row {row.sheetRow}: {row.name} &lt;{normalizeEmail(row.email)}&gt;
                {" - "}
                {companyOptions.find((o) => o.value === row.companyId)?.text}
              </li>
            ))}
          </ul>
          <p>
            {counts.error} row(s) that will not be created and {counts.excluded}{" "}
            excluded row(s) are skipped. Passwords are set exactly as shown.
          </p>
          <ModalActions>
            <StandardButton
              type="button"
              size="sm"
              outline
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </StandardButton>
            <StandardButton
              type="button"
              size="sm"
              color="green"
              onClick={handleCreate}
            >
              Create
            </StandardButton>
          </ModalActions>
        </ModalContent>
      </Modal>
    </Container>
  );
};

const Container = styled.div`
  margin: auto;
`;

const PageTitle = styled.h2`
  color: ${(props) => props.theme.colors.idataDarkGrey};
  margin-bottom: 3rem;
  text-align: center;
`;

const Intro = styled.p`
  font-size: 1.6rem;
  margin: 2rem 0;
`;

const FileInput = styled.div`
  margin: 2rem 0;
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  font-size: 1.6rem;
  gap: 2rem;
  margin: 1.5rem 0;
`;

const ErrorText = styled.p`
  color: ${(props) => props.theme.colors.red};
  font-size: 1.6rem;
  font-weight: 700;
`;

const LinkButton = styled.button`
  background: none;
  border: 0;
  color: ${(props) => props.theme.colors.idataBlue};
  cursor: pointer;
  font-size: 1.6rem;
  text-decoration: underline;
`;

const Actions = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  font-size: 1.6rem;
  gap: 1rem;
  margin: 3rem 0;
`;

const Results = styled.div`
  border-top: 2px solid ${(props) => props.theme.colors.green};
  font-size: 1.6rem;
  margin-top: 3rem;
  padding-top: 2rem;

  p {
    margin-bottom: 1rem;
  }
`;

const ModalContent = styled.div`
  font-size: 1.6rem;
  max-height: 70vh;
  max-width: 80rem;
  overflow-y: auto;

  ul {
    margin: 1.5rem 0;
    padding-left: 2rem;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

export default UploadClients;
